import { useRef, useState } from "react";
import { Stack, TextField } from "@fluentui/react";
import { SendRegular, SquareRegular } from "@fluentui/react-icons";
import Send from "../../assets/Send.svg";
import Mic from "../../assets/Mic.svg";
import MicColor from "../../assets/MicColor.svg";
import styles from "./QuestionInput.module.css";
import { ResultReason } from "microsoft-cognitiveservices-speech-sdk";
import SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { getSpeechToken } from "../../api";
interface Props {
  onSend: (question: string, id?: string) => void;
  onListen: (question: string, id?: string) => void;
  onStopSpeech: () => void;
  isSpeaking?: boolean;
  disabled: boolean;
  placeholder?: string;
  clearOnSend?: boolean;
  conversationId?: string;
}

export const QuestionInput = ({
  onSend,
  onListen,
  onStopSpeech,
  isSpeaking = false,
  disabled,
  placeholder,
  clearOnSend,
  conversationId,
}: Props) => {
  const abortFuncs = useRef([] as AbortController[]);
  const [question, setQuestion] = useState<string>("");
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  let recognizer;
  let synthesizer;
  const handleMicClick = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition(); // Create a new instance every time
    recognition.continuous = false;
    recognition.interimResults = false;
    // recognition.lang = "en-US"; // Change language if needed

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setListening(false);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setQuestion(transcript);
    };

    recognition.start();
  };

  const handleMic = async () => {
    onStopSpeech();
    let token: string;
    let region: string;
    try {
      const result = await getSpeechToken();
      token = result.token;
      region = result.region;
    } catch (e) {
      console.error("Failed to get speech token:", e);
      return;
    }

    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();

    // Create AutoDetectSourceLanguageConfig
    const autoDetectSourceLanguageConfig =
      SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages([
        "en-US",
        "ar-QA",
        "ar-EG",
      ]);

    recognizer = SpeechSDK.SpeechRecognizer.FromConfig(
      speechConfig,
      autoDetectSourceLanguageConfig,
      audioConfig
    );
    setListening(true);
    recognizer.recognizeOnceAsync(async (result: any) => {
      const languageDetectionResult =
        SpeechSDK.AutoDetectSourceLanguageResult.fromResult(result);
      const detectedLanguage = languageDetectionResult.language;
      setListening(false);
      if (result.reason === ResultReason.RecognizedSpeech) {
        onListen(result.text, conversationId);
      } else {
        console.log(
          "ERROR: Speech was cancelled or could not be recognized. Ensure your microphone is working properly."
        );
      }
    });
  };

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return;
    }

    if (conversationId) {
      onSend(question, conversationId);
    } else {
      onSend(question);
    }

    if (clearOnSend) {
      setQuestion("");
    }
  };

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setQuestion(newValue || "");
  };

  const sendQuestionDisabled = disabled || !question.trim();

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        className={styles.questionInputTextArea}
        placeholder={listening ? "Speak something..." : placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
        disabled={listening}
      />
      <div
        className={styles.questionInputSendButtonContainer}
        role="button"
        tabIndex={0}
        aria-label="Ask question button"
        onClick={sendQuestion}
        onKeyDown={(e) =>
          e.key === "Enter" || e.key === " " ? sendQuestion() : null
        }
      >
        {sendQuestionDisabled ? (
          <SendRegular className={styles.questionInputSendButtonDisabled} />
        ) : (
          <img src={Send} className={styles.questionInputSendButton} />
        )}
      </div>

      <div
        className={styles.questionInputMicButtonContainer}
        role="button"
        tabIndex={0}
        aria-label={isSpeaking ? "Stop speaking" : "Mic button"}
        onClick={isSpeaking ? onStopSpeech : undefined}
        onKeyDown={(e) =>
          isSpeaking && (e.key === "Enter" || e.key === " ")
            ? onStopSpeech()
            : null
        }
      >
        {isSpeaking ? (
          <div className={styles.stopSpeechButton}>
            <SquareRegular className={styles.stopSpeechIcon} aria-hidden="true" />
          </div>
        ) : listening ? (
          <img
            src={MicColor}
            className={`${styles.questionInputMicButton} ${
              listening ? styles.pulse : ""
            }`}
          />
        ) : (
          <img
            src={Mic}
            className={styles.questionInputMicButton}
            onClick={handleMic}
          />
        )}
      </div>
    </Stack>
  );
};
